import { memo } from "react";
import { Box, Drawer, IconButton, Stack, Typography } from "@mui/material";
import { Close } from "@mui/icons-material";
import dayjs, { type Dayjs } from "dayjs";
import type { BookingTarget } from "../../booking/types";
import { useTeleconsultoriaDateRange } from "../../shared/hooks/useTeleconsultoriaDateRange";
import { AffiliateBookingSection } from "./AffiliateBookingSection";

interface BookingPickerDrawerProps {
  open: boolean;
  /** Dia em que o painel abre (o da lista); dia passado abre em hoje. */
  initialDate: Dayjs;
  providerName: string;
  /** Muda depois de marcar/cancelar: o horário usado some/volta. */
  refreshKey: number;
  onClose: () => void;
  onBook: (target: BookingTarget) => void;
}

/** Nunca abre num dia passado: horário passado não se marca. */
function openingDate(initialDate: Dayjs): Dayjs {
  const today = dayjs().startOf("day");
  return initialDate.isBefore(today, "day") ? today : initialDate;
}

/**
 * Conteúdo do painel. Fica separado porque o Drawer temporário só monta os
 * filhos quando abre: horários (GetSlot) e presença (PeerJS) só são buscados
 * com o painel aberto, e cada abertura começa no dia pedido.
 */
const PickerContent = memo(function PickerContent({
  initialDate,
  refreshKey,
  onBook,
}: Pick<BookingPickerDrawerProps, "initialDate" | "refreshKey" | "onBook">) {
  const range = useTeleconsultoriaDateRange("day", openingDate(initialDate));
  return (
    <AffiliateBookingSection range={range} refreshKey={refreshKey} onBook={onBook} />
  );
});

/**
 * Painel "Marcar teleconsultoria" da AFILIADA: dia → teleconsultor → horário.
 * À direita no desktop; tela cheia no celular.
 */
export const BookingPickerDrawer = memo(function BookingPickerDrawer({
  open,
  initialDate,
  providerName,
  refreshKey,
  onClose,
  onBook,
}: BookingPickerDrawerProps) {
  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box
        sx={{ width: { xs: "100vw", sm: 520 }, p: 2 }}
        role="dialog"
        aria-label="Marcar teleconsultoria"
      >
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
          <Stack sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h6" fontWeight={700} noWrap>
              Marcar teleconsultoria
            </Typography>
            <Typography variant="body2" color="text.secondary" noWrap>
              Teleconsultores de {providerName}
            </Typography>
          </Stack>
          <IconButton onClick={onClose} aria-label="Fechar">
            <Close />
          </IconButton>
        </Stack>
        <PickerContent initialDate={initialDate} refreshKey={refreshKey} onBook={onBook} />
      </Box>
    </Drawer>
  );
});
